import { tokens } from '@cast/design-system';

import { DateRangeUnsupportedBanner } from '../_components/DateRangeUnsupportedBanner';
import { StackedAreaChart } from '../_components/StackedAreaChart';
import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { useInventoryWorkloadsTimeRangeSelection } from '../../../_hooks/useInventoryWorkloadsTimeRangeSelection';

import {
  GpuMemoryWasteChartPoint,
  useGpuMemoryWasteChartData,
} from './_hooks/useGpuMemoryWasteChartData';
import { isGpuDateRangeSupported } from './_utils/isGpuDateRangeSupported';

type GpuMemoryWasteChartMetricKey = 'totalWasted' | 'available' | 'used';

const METRIC_SETTINGS: Record<
  GpuMemoryWasteChartMetricKey,
  { color?: string; title: string; tooltipTitle: string }
> = {
  totalWasted: {
    title: 'TOTAL WASTED GPU MEMORY',
    tooltipTitle: 'Total wasted GPU memory',
  },
  available: {
    color: tokens.theme.data.blue300,
    title: 'AVAILABLE',
    tooltipTitle: 'Available',
  },
  used: {
    color: tokens.theme.data.green200,
    title: 'USED',
    tooltipTitle: 'Used',
  },
};

const TITLE = 'GPU Memory waste';
const TEST_ID = 'gpu-memory-waste-chart';

export const GpuMemoryWasteChart = () => {
  const { timeZone, getRange } = useInventoryWorkloadsTimeRangeSelection();
  const { from, to } = getRange();
  const { data, totals, isLoading, error, refetch } =
    useGpuMemoryWasteChartData();

  if (!isGpuDateRangeSupported(from, to)) {
    return (
      <WidgetLayout title={TITLE} testId={TEST_ID}>
        <DateRangeUnsupportedBanner testId={`${TEST_ID}-unsupported-range`} />
      </WidgetLayout>
    );
  }

  return (
    <StackedAreaChart<GpuMemoryWasteChartPoint, GpuMemoryWasteChartMetricKey>
      title={TITLE}
      testId={TEST_ID}
      data={data}
      totals={totals}
      metricSettings={METRIC_SETTINGS}
      metricKeys={['totalWasted', 'available', 'used']}
      chartMetricKeys={['available', 'used']}
      timeZone={timeZone}
      unit="GiB"
      valueType="number"
      isLoading={isLoading}
      error={error}
      refetch={refetch}
    />
  );
};
